<template>
    <div
        v-if="reservation && !reservation.is_quote"
        id="lx-online-booking-reservation"
        class="lx-online-booking-reservation"
    >
        <b-container>
            <b-row>
                <b-col md="12" style="margin-bottom:30px;" class="hidden-print">
                    <card
                        v-if="
                            reservation.payment_method_confirmation_status ===
                                PAYMENT_METHOD_CONFIRMATION_STATUS_PENDING
                        "
                        class="lx-online-booking-reservation__success-card"
                    >
                        <template #header-content>
                            Payment Method Pending Confirmation (Microdeposits)
                        </template>
                        <template #body>
                            <p>
                                Expect a $0.01 deposit to the bank account in 1-2 business days and an email with
                                additional instructions to verify it. Verification attempts have a limit of three
                                failures. If you exceed this limit, we can no longer verify the bank account. In
                                addition, microdeposit verifications have a timeout of 10 days. If you can’t verify
                                microdeposits in time, you will need to add a payment method again.
                            </p>
                        </template>
                    </card>
                </b-col>
                <b-col md="12" style="margin-bottom:30px;" class="hidden-print">
                    <card class="lx-online-booking-reservation__success-card">
                        <template #header-content>
                            Your reservation request has been submitted!
                        </template>
                        <template #body>
                            <p>{{ website.thank_you_text }}</p>
                            <p
                                v-if="website.terms"
                                class="lx-online-booking-reservation__success-card__terms"
                            >
                                {{ website.terms }}
                            </p>
                            <p
                                v-if="reservation.is_paypal"
                                class="lx-online-booking-reservation__success-card__paypal"
                            >
                                Thank you for your payment. Your transaction has
                                been completed, and a receipt for your purchase
                                has been emailed to you. You may log into your
                                account at
                                <a target="_blank" href="https://www.paypal.com"
                                    >www.paypal.com</a
                                >
                                to view details of this transaction.
                            </p>
                        </template>
                    </card>
                </b-col>
                <b-col md="12" style="margin-bottom:30px;" class="hidden-print">
                    <online-booking-reservation-optional-travel-insurance />
                </b-col>
                <b-col md="12" style="margin-bottom:30px;">
                    <card class="lx-online-booking-reservation__invoice-card" :header-content-class="['w-100']">
                        <template #header-icon>
                            <font-awesome-icon
                                class="lx-online-booking-reservation__invoice-card__header-icon" 
                                :icon="['fas', 'file-invoice']"
                                size="lg"
                            />
                        </template>
                        <template #header-content>
                            <div class="lx-online-booking-reservation__invoice-card__header-content">
                                Invoice #{{ reservationID }} 
                                <a href="javascript:window.print()" class="hidden-print">
                                    <font-awesome-icon 
                                        :icon="['fas', 'print']"
                                        size="lg"
                                    />
                                </a>
                            </div>                           
                        </template>
                        <template #body>
                            <div class="invoice-body">
                                <div class="lx-online-booking-reservation__invoice invoice-body-border" :style="borderStyle">
                                    <div class="lx-online-booking-reservation__invoice__properties__wrapper ">
                                        <div 
                                            class="lx-online-booking-reservation__invoice__properties__wrapper__property__wrapper"
                                            v-for="techReservation in reservation.tech_reservations"
                                            :key="techReservation.tech_reservation.id"
                                        >
                                            <online-booking-invoice
                                                :tech-reservation="techReservation"
                                            />
                                        </div>
                                    </div>
                                    <hr />
                                    <h4 class="lx-online-booking-reservation__invoice__information">For more information:</h4>
                                    {{ website.title }}<br />
                                    {{ website.public_email }}<br />
                                    {{ website.public_phone }}
                                </div>

                                <div class="invoice-body-guest-app" v-if="reservation.qr_code">
                                    <div style="font-size: 17px; font-family: var(--body-font-family) !important; font-weight: bold;">Download our Guest App</div>
                                    <div style="font-size: 12px; font-family: var(--body-font-family) !important; font-style: italic;">Powered by Staylio</div>
                                    <div class="invoice-guest-app-images">
                                        <div style="width: 100%; text-align: center;" class="invoice-guest-app-store-images">
                                            <div v-html="reservation.qr_code"></div>
                                        </div>
                                        <div class="invoice-guest-app-store-images">
                                            <a href="https://apps.apple.com/us/app/staylio/id6474561609" target="_blank" style="padding: 0.5rem;">
                                                <img class="lx-online-booking-reservation_invoice_app_store_img" src="https://staylio.com/wp-content/uploads/2024/04/apple-image.png" alt="Download on the App Store" />
                                            </a>
                                            <a href="https://play.google.com/store/apps/details?id=com.lodgix.guestapp&hl=en_US&gl=US" target="_blank" style="padding: 0.5rem;">
                                                <img class="lx-online-booking-reservation_invoice_app_store_img" src="https://staylio.com/wp-content/uploads/2024/04/google.png" alt="Get it on Google Play" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </card>
                </b-col>
            </b-row>
        </b-container>
    </div>
    <div v-else />
</template>

<script>
    import { mapGetters } from 'vuex';
    import Card from '../../generic/card/card.vue';
    import OnlineBookingInvoice from '../common/online-booking-invoice.vue';
    import OnlineBookingReservationOptionalTravelInsurance from './optional-travel-insurance/online-booking-reservation-optional-travel-insurance.vue';
    import { PAYMENT_METHOD_CONFIRMATION_STATUS_PENDING } from '../../../constants';


    export default {
        name: 'OnlineBookingReservation',
        components: {
            Card,
            OnlineBookingInvoice,
            OnlineBookingReservationOptionalTravelInsurance,
        },
        computed: {
            ...mapGetters('onlineBooking', ['reservation', 'website']),
            reservationID() {
                return this.$route.params.reservationId || 0;
            },
            borderStyle() {
                return this.reservation.qr_code ? 'border-right: 1px solid rgba(0,0,0,0.1);' : '';
            },
        },
        data() {
            return {
                PAYMENT_METHOD_CONFIRMATION_STATUS_PENDING: PAYMENT_METHOD_CONFIRMATION_STATUS_PENDING,
            };
        },
        mounted() {
            // Remove query string after PayPal redirect.
            try {
                this.$router.replace({ query: null });
            } catch {}

            (async () => {
                this.$store.commit('onlineBooking/gotBusy', true);
                try {
                    await this.fetchReservation();
                    if (this.reservation.is_quote) {
                        this.$router.push({ name: 'online-booking-calendar' });
                    }
                } finally {
                    this.$store.commit('onlineBooking/gotBusy', false);
                }

                setTimeout(this.sendAnalyticsPurchaseEvent, 1000);
            })();
        },
        methods: {
            async fetchReservation() {
                const id = this.reservationID;
                try {
                    await this.$store.dispatch(
                        'onlineBooking/fetchReservation',
                        { id: id, withRentalAgreement: false }
                    );
                } catch {
                    await this.$router.push({
                        name: 'online-booking-calendar',
                    });
                    this.errorToast({
                        text: `Reservation #${id} has not been found.`,
                    });
                }
            },
            sendAnalyticsPurchaseEvent() {
                if (window.gtag) {
                    try {
                        let total = 0;
                        let taxes = 0;
                        const data = {
                            transaction_id: this.reservation.id,
                            event_label: 'Lodgix Reservation',
                            value: '',
                            currency: this.reservation.currency.code,
                            items: [],
                        };

                        this.reservation.tech_reservations.forEach(
                            techReservation => {
                                const item = {
                                    id: techReservation.property.id,
                                    name: techReservation.property.name,
                                    brand: '',
                                    quantity: 1,
                                    price:
                                        techReservation.total.discounted_rent,
                                };
                                if (techReservation.discount_code) {
                                    item['coupon'] =
                                        techReservation.discount_code;
                                }
                                data['items'].unshift(item);
                                total += techReservation.total.gross;
                                taxes += techReservation.total.taxes;
                            }
                        );

                        data['value'] = total;
                        data['tax'] = taxes;

                        window.gtag('event', 'purchase', data);
                    } catch (err) {}
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    #lx-online-booking-reservation .lx-online-booking-reservation {
        &__invoice-card {
            &__header-content{
                display: flex;
                width: 100%;
                a {
                    margin-left: auto;
                    margin-right: 1.5rem;
                    font-size: 18px;
                    color: #fff;
                }
            }
        }
        &__invoice {
            &__properties__wrapper {
                margin-bottom: 2rem;
                &__property__wrapper {
                    margin-bottom: 4rem;
                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }
            &__information {
                font-weight: bold;
                font-size: 16px;
            }
        }
        &__success-card {
            p {
                margin-bottom: 0;
            }

            &__terms,
            &__paypal {
                margin-top: 1rem;
            }
        }
    }
    @media print {
        .hidden-print {
            display: none !important;
        }
        #lx-online-booking-reservation .lx-online-booking-reservation {
            &__invoice-card {
                &__header-content{
                    color: #000 !important;
                }
                &__header-icon{
                    color: #000 !important;
                }
            }
        }
    }

    .lx-online-booking-reservation_invoice_app_store_img {
        width: 14rem;
    }

    .invoice-body {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .invoice-body-border {
        width: 100%;
    }

    .invoice-body-guest-app {
        width: 100%;
        padding-left: 1.5rem;
    }

    .invoice-guest-app-images {
        display: flex;
        height: 190px;
        align-items: center;
    }

    .invoice-guest-app-store-images {
        width: 100%;
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
    }

    @media (max-width: 991px) {
        .invoice-body {
            flex-direction: column;
        }
        .invoice-body-border {
            border-right: none !important;
            margin-bottom: 1rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid rgba(0,0,0,0.1);
        }
        .invoice-body-guest-app {
            padding-left: 0;
        }

    }

    @media (max-width: 767px) {
        .invoice-guest-app-images {
            flex-direction: column;
            height: auto;
        }
    }
</style>
